import React from 'react';
import type { IntlShape } from 'react-intl-next';

import type {
	ExtensionComponentProps,
	ExtensionManifest,
} from '@atlaskit/editor-common/extensions/extension-manifest';
import TabsIcon from '@atlaskit/icon-lab/core/tabs';

import { EXTENSION_KEY, EXTENSION_TYPE } from '../nativeTabsConstants.ts';
import type {
	GetCopyLinkBaseUrl,
	NativeTabsParameters,
	ResolveElementByHash,
} from '../nativeTabsTypes.ts';
import { buildDefaultParameters } from '../utils/tabParameters.ts';

import { buildBaseManifest } from './baseManifest.tsx';

type NativeTabsEditorManifestOptions = {
	getCopyLinkBaseUrl?: GetCopyLinkBaseUrl;
	resolveElementByHash?: ResolveElementByHash;
};

/**
 * Editor-side manifest for the Native Tabs extension. Adds the slash-menu
 * `quickInsert` entry and points `render()` at `TabsEditor`.
 *
 * The renderer-side counterpart lives in `rendererManifest.tsx`.
 */
export const getNativeTabsEditorManifest = (
	intl: IntlShape,
	{ getCopyLinkBaseUrl, resolveElementByHash }: NativeTabsEditorManifestOptions = {},
): ExtensionManifest<NativeTabsParameters> => {
	const base = buildBaseManifest(intl);
	const Icon = () => <TabsIcon label={base.title} />;

	return {
		...base,
		modules: {
			quickInsert: [
				{
					key: EXTENSION_KEY,
					title: base.title,
					description: base.description,
					icon: async () => Icon,
					action: async () => {
						const { tabs, frames } = buildDefaultParameters();
						return {
							type: 'multiBodiedExtension',
							attrs: {
								extensionType: EXTENSION_TYPE,
								extensionKey: EXTENSION_KEY,
								parameters: {
									extensionTitle: base.title,
									tabs,
								},
							},
							content: frames,
						};
					},
				},
			],
			nodes: {
				default: {
					type: 'multiBodiedExtension',
					// @ts-expect-error — `__allowBodiedOverride` is recognised by the MBE plugin
					// at runtime but isn't yet on `ExtensionModuleNode`. Remove when upstream types update.
					__allowBodiedOverride: true,
					// editor-core's MBE plugin mounts this with `{ node, actions, ... }` injected.
					render: async () => {
						const mod = await import(
							/* webpackChunkName: "loadable-NativeTabsEditor" */ '../components/TabsEditor.tsx'
						);
						return (
							props: ExtensionComponentProps<NativeTabsParameters>,
						): React.JSX.Element | null => {
							if (!props.actions) {
								return null;
							}

							return (
								<mod.TabsEditor
									{...props}
									actions={props.actions}
									getCopyLinkBaseUrl={getCopyLinkBaseUrl}
									resolveElementByHash={resolveElementByHash}
								/>
							);
						};
					},
				},
			},
		},
	};
};
